import { api, formatError } from '../api-client'
import { flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Audit CLI — read the workspace audit log (owner/admin). The server gates
// access; the CLI just relays the verdict and renders the entries.

const KNOWN_AUDIT_FLAGS: ReadonlySet<string> = new Set(['limit', 'json', 'help'])

export function assertKnownAuditFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_AUDIT_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_AUDIT_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless audit':\n` +
      lines.join('\n') +
      `\nRun 'driftless audit --help' for valid flags.`,
  )
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so AUDIT_HELP still resolves from './audit'.
export { AUDIT_HELP } from './help-text'
import { AUDIT_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(AUDIT_HELP)
}

export async function auditCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownAuditFlags(flags)

  const ws = await resolveWorkspaceSlug()

  try {
    switch (sub) {
      case 'log': {
        const params: string[] = []
        if (flags['limit']) params.push(`limit=${encodeURIComponent(String(flags['limit']))}`)
        const q = params.length ? `?${params.join('&')}` : ''
        const result = (await api.get(`/workspaces/${ws}/audit-log${q}`)) as any
        const rows = Array.isArray(result) ? result : (result?.entries ?? result?.log ?? [])
        if (isJSON) { emitJSON(result); break }
        if (!rows.length) { console.log('No audit-log entries.'); break }
        for (const e of rows) {
          const action = (e.action ?? e.event ?? e.type ?? '?').toString().padEnd(20)
          const actor = e.actor ?? e.actor_email ?? e.clerk_id ?? ''
          console.log(`${e.created_at ?? ''}  ${action} ${actor}`)
        }
        break
      }
      default:
        console.error(`Unknown audit subcommand: '${sub}'. Run 'driftless audit --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}
